import { TZDateMini } from '@date-fns/tz';

import {
  format,
  units,
  formatDate,
  abbreviateNumber,
  adjustFractionDigits,
} from '@dynatrace-sdk/units';
import { getTimezone } from '@dynatrace-sdk/user-preferences';

/**
 * @public
 */
export const formatterOptions = [
  'bit',
  'byte',
  'duration',
  'percent',
  'number',
  'rate',
  'date',
  'customUnit',
] as const;

/**
 * @public
 */
export type FormatterType = (typeof formatterOptions)[number];

/**
 * @public
 */
export interface FormatByTypeNumericOptions {
  type: 'number';
  precision?: number;
  abbreviate?: boolean;
}

/**
 * @public
 */
export interface FormatByTypeBitOptions {
  type: 'bit';
  bitOutputUnit?: keyof typeof units.data;
  precision?: number;
}

/**
 * @public
 */
export interface FormatByTypeByteOptions {
  type: 'byte';
  byteOutputUnit?: keyof typeof units.data;
  precision?: number;
}

/**
 * @public
 */
export interface FormatByTypeDurationOptions {
  type: 'duration';
  precision?: number;
  /** Mode of type DurationMode (number) */
  formatMode?: number;
  /** Unit of type the duration is in */
  inputUnit?: keyof typeof units.time;
  /** Unit of type the output shall transform to. */
  outputUnit?: keyof typeof units.time;
}

/**
 * @public
 */
export interface FormatByTypePercentOptions {
  type: 'percent';
  precision?: number;
}

/**
 * @public
 */
export interface FormatByTypeRateOptions {
  type: 'rate';
  /** Denominator unit. */
  denominatorUnit?: string;
  /** Numerator unit. */
  numeratorUnit?: string;
  /** Precision used to adjust the fraction digits of the passed number. */
  precision?: number;
}

/**
 * @public
 */
export interface FormatByTypeRateAlternativeOptions {
  type: 'rate';
  /** Denominator unit. */
  denominatorUnit?: string;
  /** Precision used to adjust the fraction digits of the passed number. */
  precision?: number;
}

/**
 * @public
 */
export interface FormatByTypeDateOptions {
  type: 'date';

  /**
   * If true then the format will be optimized for better readability in table
   * columns or lists (textual month names and alignment):
   * <pre>
   * Aug 04, 2020, 07:30 AM
   * Jul 24, 2020, 03:30 PM
   * </pre>
   * In this case also the year is omitted if the given date is in the current one.
   * <pre>
   * Example (current year is 2021):
   * Aug 04, 07:30 AM                (2021-08-04T07:30:55.000Z)
   * Jul 19, 02:34 PM                (2021-07-19T14:34:06.000Z)
   * Nov 10, 2020, 11:55 AM          (2020-11-10T11:55:18.000Z)
   * </pre>
   */
  listFormat?: boolean;
}

/**
 * @public
 */
export interface FormatByCustomUnitOptions {
  type: 'customUnit';
  /** Custom unit name. */
  unit?: string;
  /** Precision used to adjust the fraction digits of the passed number. */
  precision?: number;
}

/**
 * @public
 */
export type FormatByTypeRateFullOptions = FormatByTypeRateOptions &
  FormatByTypeRateAlternativeOptions;

/**
 * @public
 */
export type FormatterOptions =
  | FormatByTypeNumericOptions
  | FormatByTypeBitOptions
  | FormatByTypeByteOptions
  | FormatByTypeDurationOptions
  | FormatByTypePercentOptions
  | FormatByTypeRateFullOptions
  | FormatByTypeDateOptions
  | FormatByCustomUnitOptions;

/**
 * Type-guard for numeric options
 * @param options - Formatter options
 * @returns boolean
 */
function isNumericFormatterOptions(
  options: FormatterOptions,
): options is FormatByTypeNumericOptions {
  return (options as FormatByTypeNumericOptions).type === 'number';
}

/**
 * Type-guard for bit options
 * @param options - Formatter options
 * @returns boolean
 */
function isBitFormatterOptions(
  options: FormatterOptions,
): options is FormatByTypeBitOptions {
  return (options as FormatByTypeBitOptions).type === 'bit';
}

/**
 * Type-guard for byte options
 * @param options - Formatter options
 * @returns boolean
 */
function isByteFormatterOptions(
  options: FormatterOptions,
): options is FormatByTypeByteOptions {
  return (options as FormatByTypeByteOptions).type === 'byte';
}

/**
 * Type-guard for duration options
 * @param options - Formatter options
 * @returns boolean
 */
function isDurationFormatterOptions(
  options: FormatterOptions,
): options is FormatByTypeDurationOptions {
  return (options as FormatByTypeDurationOptions).type === 'duration';
}

/**
 * Type-guard for percent options
 * @param options - Formatter options
 * @returns boolean
 */
function isPercentFormatterOptions(
  options: FormatterOptions,
): options is FormatByTypePercentOptions {
  return (options as FormatByTypePercentOptions).type === 'percent';
}

/**
 * Type-guard for rate options
 * @param options - Formatter options
 * @returns boolean
 */
function isRateFormatterOptions(
  options: FormatterOptions,
): options is FormatByTypeRateOptions {
  const rateOptions = options as FormatByTypeRateOptions;
  return rateOptions.type === 'rate' && rateOptions.numeratorUnit !== undefined;
}

/**
 * Type-guard for rate options
 * @param options - Formatter options
 * @returns boolean
 */
function isRateFormatterAlternativeOptions(
  options: FormatterOptions,
): options is FormatByTypeRateAlternativeOptions {
  const rateOptions = options as FormatByTypeRateAlternativeOptions;
  return rateOptions.type === 'rate';
}

/**
 * Type-guard for date options
 * @param options - Formatter options
 * @returns boolean
 */
function isDateFormatterOptions(
  options: FormatterOptions,
): options is FormatByTypeDateOptions {
  const timeOptions = options as FormatByTypeDateOptions;
  return timeOptions.type === 'date';
}

/**
 * Type-guard for custom unit options
 * @param options - Formatter options
 * @returns boolean
 */
function isCustomUnitFormatterOptions(
  options: FormatterOptions,
): options is FormatByCustomUnitOptions {
  const timeOptions = options as FormatByCustomUnitOptions;
  return timeOptions.type === 'customUnit';
}

/**
 * Format the value based on the type the data should be shown in
 *  @deprecated please use the new formatters SDK instead
 * TODO?: Can't be removed yet because its still used by some chart components without any substitution in the units SDK.

* @public
*/
export function formatByType(
  value: number,
  options: FormatterOptions = { type: 'number' },
): string {
  if (isNumericFormatterOptions(options)) {
    const { abbreviate, precision } = options;
    if (abbreviate === true) {
      const formattedNumber = abbreviateNumber(value, undefined, {
        minimumFractionDigits: precision,
        maximumFractionDigits: precision,
      });
      return `${formattedNumber.formattedValue}${formattedNumber.postfix}`;
    } else {
      return adjustFractionDigits(value, {
        minimumFractionDigits: precision,
        maximumFractionDigits: precision,
      });
    }
  } else if (isBitFormatterOptions(options)) {
    const { bitOutputUnit, precision } = options;
    return format(value, {
      input: units.data.bit,
      output: bitOutputUnit ? units.data[bitOutputUnit] : undefined,
      abbreviate: false,
      maximumFractionDigits: precision,
    });
  } else if (isByteFormatterOptions(options)) {
    const { byteOutputUnit, precision } = options;
    return format(value, {
      input: units.data.byte,
      output: byteOutputUnit ? units.data[byteOutputUnit] : undefined,
      maximumFractionDigits: precision,
    });
  } else if (isDurationFormatterOptions(options)) {
    const { precision, formatMode, inputUnit, outputUnit } = options;

    const inputFilterUnit = (
      inputUnit ? units.time[inputUnit] : units.time.millisecond
    ) as typeof units.time.second;

    return format(value, {
      maximumFractionDigits: precision,
      cascade: formatMode as never,
      input: inputFilterUnit,
      output: outputUnit ? units.time[outputUnit] : undefined,
    });
  } else if (isPercentFormatterOptions(options)) {
    const precision = options.precision;
    return format(value, {
      maximumFractionDigits: precision,
      suffix: '%',
    });
  } else if (isRateFormatterOptions(options)) {
    const { precision, denominatorUnit, numeratorUnit } = options;
    const numerator = typeof numeratorUnit === 'string' ? numeratorUnit : '';
    const usePrecision =
      typeof numeratorUnit === 'number' ? numeratorUnit : precision;
    return format(value, {
      suffix: `${numerator}/${denominatorUnit}`,
      maximumFractionDigits: usePrecision || precision,
      abbreviate: false,
    });
  } else if (isRateFormatterAlternativeOptions(options)) {
    const { precision, denominatorUnit } = options;

    return format(value, {
      suffix: `/${denominatorUnit}`,
      maximumFractionDigits: precision,
      abbreviate: false,
    });
  } else if (isDateFormatterOptions(options)) {
    const timeZone = getTimezone();
    return formatDate(new TZDateMini(value, timeZone), {
      day: '2-digit',
      month: 'short',
      year:
        new TZDateMini(value, timeZone).getFullYear() ===
        TZDateMini.tz(timeZone).getFullYear()
          ? undefined
          : 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second:
        new TZDateMini(value, timeZone).getSeconds() === 0
          ? undefined
          : 'numeric',
    });
  } else if (isCustomUnitFormatterOptions(options)) {
    const { unit, precision } = options;
    return format(value as number, {
      suffix: unit,
      abbreviate: true,
      maximumFractionDigits: precision,
    });
  }
  return format(value as number, {
    abbreviate: true,
    maximumFractionDigits: 2,
  });
}
